import React from 'react'
import { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import { useRouter } from 'next/router'
import ErrorPage from '../../404'
import { initializeApollo } from '../../../lib/apolloClient'

import {
  ELEMENTS,
  PRODUCT,
  SECTION,
  SECTIONS,
} from '../../../queries/catalogQuery'
import { section, sectionVariables } from '../../../__generated__/section'
import { sections, sectionsVariables } from '../../../__generated__/sections'
import { elements, elementsVariables } from '../../../__generated__/elements'
import {
  quiz_sections,
  quiz_sectionsVariables,
} from '../../../__generated__/quiz_sections'
import { ELEMENTS_ONPAGE } from '../../../contexts/sectionProvider'
import {
  getElementVariables,
  getParentSectionElementsVariables,
  getSubSectionElementsVariables,
  getSectionVariables,
  getSubSectionsVariables,
  getAllSectionsVariables,
} from '../../../lib/gql'
import SectionProvider from '../../../contexts/sectionProvider'
import Section from '../../../components/catalog/Section/Section'
import globalCache from '../../../lib/globalCache'
import { SortOrder } from '../../../__generated__/globalTypes'
import ProductDetailProvider from '../../../contexts/productDetailProvider'
import { product, productVariables } from '../../../__generated__/product'
import { HeaderColors } from '../../../components/types'
import { QUIZ_SECTIONS } from '../../../queries/quizQuery'
import { getOptimizedImageSrc } from '../../../lib/utils'
import sanitizeHtml from 'sanitize-html'
import ProductPage from '../../../components/catalog/Product'
import { REVIEWS_FILES } from '../../../queries/reviewQuery'
import {
  reviewsFiles,
  reviewsFilesVariables,
} from '../../../__generated__/reviewsFiles'

const CatalogSectionPage: NextPage<{ now: string }> = ({ now }) => {
  const router = useRouter()
  const { section, path } = router.query
  let subsectionCode: string | undefined
  let elementCode: string | undefined

  if (path?.length === 1) {
    subsectionCode = path[0]
  } else if (path?.length === 2) {
    subsectionCode = path[0]
    elementCode = path[1]
  } else if (path?.length > 2) {
    return <ErrorPage />
  }
  if (!section || typeof section !== 'string') {
    return <ErrorPage />
  }

  return elementCode ? (
    <ProductDetailProvider
      elementCode={elementCode}
      sectionCode={subsectionCode}
      parentSectionCode={section}
      today={now}
    >
      <ProductPage code={elementCode} />
    </ProductDetailProvider>
  ) : (
    <SectionProvider
      now={now}
      sectionCode={section}
      subSectionCode={subsectionCode}
    >
      <Section />
    </SectionProvider>
  )
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { section: sectionCode, path } = params
  if (!sectionCode || typeof sectionCode !== 'string') {
    return <ErrorPage />
  }
  let subsectionCode: string | undefined
  let elementCode: string | undefined

  if (path?.length === 1) {
    subsectionCode = path[0]
  } else if (path?.length === 2) {
    subsectionCode = path[0]
    elementCode = path[1]
  } else if (path?.length > 2) {
    return <ErrorPage />
  }

  const now = new Date().toISOString()

  const apolloClient = initializeApollo()
  await globalCache(apolloClient)

  let noData = false

  let seo

  if (elementCode) {
    const [data] = await Promise.all([
      apolloClient.query<product, productVariables>({
        query: PRODUCT,
        variables: getElementVariables({
          elementCode,
          sectionCode: subsectionCode || sectionCode,
          today: now,
        }),
      }),

      // subsections info
      apolloClient.query<sections, sectionsVariables>({
        query: SECTIONS,
        variables: getSubSectionsVariables({ sectionCode }),
      }),
    ])

    if (!data?.data?.product) {
      noData = true
    } else {
      await apolloClient.query<reviewsFiles, reviewsFilesVariables>({
        query: REVIEWS_FILES,
        variables: {
          element: {
            iblock: { bx_id: { equals: 6 } },
            active: { equals: true },
            propertyValuesRelation: {
              some: {
                property: { code: { equals: 'ELEMENT' } },
                targetElement: {
                  active: { equals: true },
                  iblock: { bx_id: { equals: 42 } },
                  id: { equals: data.data.product.id },
                },
              },
            },
          },
        },
      })
    }

    seo = {
      title: data?.data?.product?.seo?.elementMetaTitle,
      description: sanitizeHtml(
        data?.data?.product?.seo?.elementMetaDescription
          .replace(/&lt;/gs, '<')
          .replace(/&gt;/gs, '>')
          .replace(/&quot;/gs, '"')
          .replace(/\n/gs, ''),
        { allowedTags: [] },
      ),
      keywords: data?.data?.product?.seo?.elementMetaKeywords,
      ogImage:
        getOptimizedImageSrc(data?.data?.product?.previewPicture?.url) ||
        getOptimizedImageSrc(data?.data?.product?.previewPicture?.url) ||
        null,
    }
  } else if (subsectionCode) {
    await Promise.all([
      // parent section info
      apolloClient
        .query<section, sectionVariables>({
          query: SECTION,
          variables: getSectionVariables({ sectionCode: subsectionCode }),
        })
        .then((res) => {
          noData = !res?.data?.sections?.nodes?.length
          seo = {
            title: res?.data?.sections?.nodes[0]?.seo?.sectionMetaTitle,
            description:
              res?.data?.sections?.nodes[0]?.seo?.sectionMetaDescription,
            keywords: res?.data?.sections?.nodes[0]?.seo?.sectionMetaKeywords,
          }
        }),
      // subsections info
      apolloClient.query<sections, sectionsVariables>({
        query: SECTIONS,
        variables: getSubSectionsVariables({ sectionCode }),
      }),
      // subsection elements
      apolloClient.query<elements, elementsVariables>({
        query: ELEMENTS,
        variables: getSubSectionElementsVariables({
          first:
            sectionCode === 'mineralnaya_kosmetika_dlya_litsa'
              ? ELEMENTS_ONPAGE - 1
              : ELEMENTS_ONPAGE,
          sectionCode: subsectionCode,
          order: { sort: SortOrder.asc },
          now,
        }),
      }),
      //quiz
      sectionCode === 'mineralnaya_kosmetika_dlya_litsa'
        ? apolloClient.query<quiz_sections, quiz_sectionsVariables>({
            query: QUIZ_SECTIONS,
            variables: {
              shadesWhere: {
                iblock: { code: { equals: 'quiz-shades' } },
                level: { equals: 1 },
              },
              typesWhere: {
                iblock: { code: { equals: 'quiz-types' } },
                level: { equals: 1 },
              },
              first: 999,
            },
          })
        : null,
    ])
  } else {
    await Promise.all([
      // parent section info
      apolloClient
        .query<section, sectionVariables>({
          query: SECTION,
          variables: getSectionVariables({ sectionCode }),
        })
        .then((res) => {
          noData = !res?.data?.sections?.nodes?.length
          seo = {
            title: res?.data?.sections?.nodes[0]?.seo?.sectionMetaTitle,
            description:
              res?.data?.sections?.nodes[0]?.seo?.sectionMetaDescription,
            keywords: res?.data?.sections?.nodes[0]?.seo?.sectionMetaKeywords,
          }
        }),
      // subsections list
      apolloClient.query<sections, sectionsVariables>({
        query: SECTIONS,
        variables: getSubSectionsVariables({ sectionCode }),
      }),
      // parent section elements
      apolloClient.query<elements, elementsVariables>({
        query: ELEMENTS,
        variables: getParentSectionElementsVariables({
          first:
            sectionCode === 'mineralnaya_kosmetika_dlya_litsa'
              ? ELEMENTS_ONPAGE - 1
              : ELEMENTS_ONPAGE,
          sectionCode,
          order: { sort: SortOrder.asc },
          now,
        }),
      }),
      //quiz
      sectionCode === 'mineralnaya_kosmetika_dlya_litsa'
        ? apolloClient.query<quiz_sections, quiz_sectionsVariables>({
            query: QUIZ_SECTIONS,
            variables: {
              shadesWhere: {
                iblock: { code: { equals: 'quiz-shades' } },
                level: { equals: 1 },
              },
              typesWhere: {
                iblock: { code: { equals: 'quiz-types' } },
                level: { equals: 1 },
              },
              first: 999,
            },
          })
        : null,
    ])
  }

  if (sectionCode === 'julirea') {
    noData = true
  }

  if (noData) {
    return {
      notFound: true,
      revalidate: 60,
    }
  }

  return {
    props: {
      __APOLLO_STATE__: apolloClient.cache.extract(),
      headerInitColor: HeaderColors.white,
      showInnerNav: !!sectionCode,
      seo: elementCode ? { ...seo, isProduct: true } : seo,
      now,
    },
    revalidate: 60,
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const paths: { params: { section: string; path: string[] } }[] = []

  const apolloClient = initializeApollo()

  await apolloClient
    .query<sections, sectionsVariables>({
      query: SECTIONS,
      variables: getAllSectionsVariables(),
    })
    .then((data) => {
      const sections = data?.data?.sections?.nodes?.filter(
        (section) => section.level === 1,
      )

      sections?.map((section) => {
        paths.push({
          params: { section: section.code, path: [] },
        })

        sections
          ?.filter(
            (item) =>
              item.level === 2 && item.parentSection.code === section.code,
          )
          .map((subSection) => {
            paths.push({
              params: { section: section.code, path: [subSection.code] },
            })
          })
      })
    })

  return {
    paths,
    fallback: 'blocking',
  }
}

export default CatalogSectionPage
